import { useEffect, useRef, useState } from 'react';
import { ApiError, type ApiClient, type HealthSnapshot } from '../auth/apiClient.js';

/**
 * SYSTEM-HEALTH — the admin backend-health poller. Runs ONLY when `enabled` (admin + signed-in +
 * API configured); non-admins never poll, so there is zero overhead for them. Uses setTimeout
 * recursion (not setInterval) so a slow/failed request can't stack. The `down`/`degraded` status is
 * the one the server already derived (`snapshot.status`); the hook only layers the client-only
 * `unknown` (device offline) and `down` (network/5xx) cases on top.
 */
export type SystemHealthStatus = 'ok' | 'degraded' | 'down' | 'unknown';

export interface UseSystemHealth {
  status: SystemHealthStatus;
  snapshot?: HealthSnapshot;
  /** True once the server answered 403 — the caller isn't actually an admin; polling stops. */
  forbidden: boolean;
}

type HealthApi = Pick<ApiClient, 'getHealth'>;

const POLL_OK_MS = 60_000;
const POLL_UNHEALTHY_MS = 30_000;

export function useSystemHealth(api: HealthApi | null, enabled: boolean): UseSystemHealth {
  const [status, setStatus] = useState<SystemHealthStatus>('unknown');
  const [snapshot, setSnapshot] = useState<HealthSnapshot | undefined>(undefined);
  const [forbidden, setForbidden] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!api || !enabled) {
      setStatus('unknown');
      setSnapshot(undefined);
      return;
    }
    let cancelled = false;
    let stopped = false;

    const schedule = (delay: number): void => {
      if (cancelled || stopped) return;
      timer.current = setTimeout(() => void tick(), delay);
    };

    const tick = async (): Promise<void> => {
      try {
        const snap = await api.getHealth();
        if (cancelled) return;
        setSnapshot(snap);
        setStatus(snap.status);
        schedule(snap.status === 'ok' ? POLL_OK_MS : POLL_UNHEALTHY_MS);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 0) {
          // Device offline — not a backend failure; don't pulse red.
          setStatus('unknown');
          schedule(POLL_UNHEALTHY_MS);
        } else if (err instanceof ApiError && err.status === 403) {
          // Not actually an admin — stop polling for good.
          setForbidden(true);
          setStatus('unknown');
          stopped = true;
        } else {
          // Network error or 5xx — the backend is unreachable/erroring.
          setStatus('down');
          setSnapshot(undefined);
          schedule(POLL_UNHEALTHY_MS);
        }
      }
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [api, enabled]);

  return { status, snapshot, forbidden };
}
